import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import ScoreAppHeader from '../../../shared/components/layout/ScoreAppHeader';
import Button from '../../../shared/components/ui/Button';
import Toggle from '../../../shared/components/ui/Toggle';
import Toast from '../../../shared/components/ui/Toast';
import { extractOrgIdFromPath } from '../../../shared/utils/organizationUtils';
import { useTournamentDetails } from '../../../shared/hooks/useTournaments';
import { tournamentService } from '../../../shared/services/tournaments';

const TournamentDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { tournamentId } = useParams<{ tournamentId: string }>();
  const [activeTab, setActiveTab] = useState<'details' | 'tab2' | 'tab3'>('details');
  const { tournament, loading, error } = useTournamentDetails(tournamentId);

  // Form state for editable fields
  const [formData, setFormData] = useState({
    slug: '',
    fixtures_enabled: false,
    standings_enabled: false
  });

  // Track if form has been modified
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Save state
  const [isSaving, setIsSaving] = useState(false);

  // Toast state
  const [toast, setToast] = useState<{
    isVisible: boolean;
    type: 'success' | 'error' | 'info';
    message: string;
  }>({
    isVisible: false,
    type: 'success',
    message: ''
  });

  // Extract organization ID from URL
  const orgIdFromPath = extractOrgIdFromPath(location.pathname);

  // Handle hash-based tab routing
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash === 'details' || hash === 'tab2' || hash === 'tab3') {
      setActiveTab(hash as 'details' | 'tab2' | 'tab3');
    } else {
      setActiveTab('details');
    }
  }, [location.hash]);

  // Populate form data when tournament loads
  useEffect(() => {
    if (tournament) {
      setFormData({
        slug: tournament.slug || '',
        fixtures_enabled: tournament.fixtures_enabled || false,
        standings_enabled: tournament.standings_enabled || false
      });
      setHasUnsavedChanges(false);
    }
  }, [tournament]);

  const handleGoBack = () => {
    const actualOrgId = orgIdFromPath || 'default';
    navigate(`/o/${actualOrgId}/cms/tournaments`);
  };

  const handleTabChange = (tabId: 'details' | 'tab2' | 'tab3') => {
    setActiveTab(tabId);
    // Update URL hash without causing a page reload
    window.history.replaceState(null, '', `${location.pathname}#${tabId}`);
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasUnsavedChanges(true);
  };

  const handleSave = async () => {
    if (!tournament) return;

    setIsSaving(true);
    try {
      const result = await tournamentService.updateTournamentDetails(tournament.id, formData);
      
      if (result.success) {
        setHasUnsavedChanges(false);
        setToast({
          isVisible: true,
          type: 'success',
          message: 'Tournament details saved successfully!'
        });
      } else {
        setToast({
          isVisible: true,
          type: 'error',
          message: result.error || 'Failed to save tournament details. Please try again.'
        });
      }
    } catch (error) {
      setToast({
        isVisible: true,
        type: 'error',
        message: 'Failed to save tournament details. Please try again.'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const tabs = [
    { id: 'details', label: 'Details', icon: '⚙️' },
    { id: 'tab2', label: 'Tab 2', icon: '📊' },
    { id: 'tab3', label: 'Tab 3', icon: '🏆' }
  ] as const;

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading tournament details...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Tournament</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button
            onClick={handleGoBack}
            variant="primary"
            size="md"
          >
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  // Show not found state
  if (!tournament) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-400 text-6xl mb-4">🏆</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Tournament Not Found</h2>
          <p className="text-gray-600 mb-4">The tournament you're looking for doesn't exist or you don't have access to it.</p>
          <Button
            onClick={handleGoBack}
            variant="primary"
            size="md"
          >
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <ScoreAppHeader
        leftContent={
          <Button
            onClick={handleGoBack}
            variant="ghost"
            size="md"
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
            }
            iconPosition="left"
          >
            Go Back
          </Button>
        }
        centerContent={
          <div className="flex items-center justify-center flex-1">
            <div className="flex items-center space-x-3">
              {/* Tournament Logo */}
              <div className="w-10 h-10 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg flex items-center justify-center">
                {tournament.logo ? (
                  <img 
                    src={tournament.logo} 
                    alt={tournament.name}
                    className="w-8 h-8 object-contain"
                  />
                ) : (
                  <span className="text-blue-600 text-lg font-bold">
                    {tournament.name.charAt(0)}
                  </span>
                )}
              </div>
              
              <div>
                <h1 className="text-xl font-semibold text-gray-900">{tournament.name}</h1>
                <div className="flex items-center space-x-2 text-sm text-gray-500">
                  <span>• {tournament.country || 'International'}</span>
                  <span>Tournament Details</span>
                </div>
              </div>
            </div>
          </div>
        }
        rightContent={
          <Button
            onClick={handleSave}
            variant="primary"
            size="md"
            disabled={isSaving}
            icon={
              isSaving ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              )
            }
            iconPosition="left"
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        }
      />

      {/* Tabs Section */}
      <div className="px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-6 pb-6">
        <div className="max-w-7xl mx-auto">
          {activeTab === 'details' && (
            <div className="space-y-6">
              {/* Tournament Details Form */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Tournament Details</h3>
                  
                  {/* Status Message */}
                  {hasUnsavedChanges ? (
                    <div className="flex items-center text-orange-600 text-sm">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      <span className="font-medium">Edits need to be saved</span>
                    </div>
                  ) : (
                    <div className="flex items-center text-green-600 text-sm">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="font-medium">All data synced</span>
                    </div>
                  )}
                </div>
                <div className="px-6 py-6">
                  <form className="space-y-6">
                    {/* Tournament Logo and Name - Display Only */}
                    <div className="flex items-center space-x-4 py-4">
                      {tournament.logo ? (
                        <img
                          src={tournament.logo}
                          alt={tournament.name}
                          className="w-16 h-16 object-contain border border-gray-300 rounded-md"
                        />
                      ) : (
                        <div className="w-16 h-16 bg-gray-100 border border-gray-300 rounded-md flex items-center justify-center">
                          <span className="text-gray-400 text-sm">No Logo</span>
                        </div>
                      )}
                      <div>
                        <h3 className="text-xl font-semibold text-gray-900">{tournament.name}</h3>
                        <p className="text-sm text-gray-500">{tournament.country}</p>
                      </div>
                    </div>

                    {/* Slug - Editable */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Slug
                      </label>
                      <input
                        type="text"
                        value={formData.slug}
                        onChange={(e) => handleInputChange('slug', e.target.value)}
                        placeholder="Enter tournament slug"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    {/* Enable Fixtures Page - Toggle */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Enable Fixtures Page
                      </label>
                      <div className="flex items-center space-x-3">
                        <Toggle
                          checked={formData.fixtures_enabled}
                          onChange={(checked) => handleInputChange('fixtures_enabled', checked)}
                          size="md"
                        />
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          formData.fixtures_enabled 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {formData.fixtures_enabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                    </div>

                    {/* Enable Standings Page - Toggle */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Enable Standings Page
                      </label>
                      <div className="flex items-center space-x-3">
                        <Toggle
                          checked={formData.standings_enabled}
                          onChange={(checked) => handleInputChange('standings_enabled', checked)}
                          size="md"
                        />
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          formData.standings_enabled 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {formData.standings_enabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'tab2' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Tab 2 Content</h3>
              <p className="text-gray-600">This is dummy content for Tab 2. We'll implement this functionality later.</p>
            </div>
          )}

          {activeTab === 'tab3' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Tab 3 Content</h3>
              <p className="text-gray-600">This is dummy content for Tab 3. We'll implement this functionality later.</p>
            </div>
          )}
        </div>
      </div>

      {/* Toast Notification */}
      <Toast
        isVisible={toast.isVisible}
        type={toast.type}
        message={toast.message}
        onClose={() => setToast(prev => ({ ...prev, isVisible: false }))}
      />
    </div>
  );
};

export default TournamentDetailPage;
